import React, { useEffect, useMemo, useState } from "react";
import { Lightbulb } from "lucide-react";

type SentenceExerciseProps = {
    exercises: string[];
    answers: string[];
    showAnswers?: boolean;
    onCheckedAll?: (stats: { correct: number; total: number }) => void;
    disabled?: boolean;
    className?: string;
};

type ItemState = {
    value: string;
    feedback: "" | "correct" | "wrong";
    showHint: boolean;
};

// ✅ Wersja zgodna z TS bez \p{L} – obejmuje Latin-1 + Latin Extended (PL/CZ)
const LETTERS_LATIN = /[^A-Za-zÀ-ÖØ-öø-ÿĀ-žƀ-ɏ0-9\s-]/g;

const normalize = (s: string) =>
    s.toLowerCase().trim().replace(LETTERS_LATIN, "");

const splitVariants = (s: string) =>
    s
        .split("|")
        .map((v) => normalize(v))
        .filter(Boolean);

const SentenceExercise: React.FC<SentenceExerciseProps> = ({
    exercises,
    answers,
    showAnswers = false,
    onCheckedAll,
    disabled = false,
    className = "",
}) => {
    const total = Math.min(exercises.length, answers.length);

    const [items, setItems] = useState<ItemState[]>(
        () =>
            new Array(total).fill(0).map(
                () =>
                    ({
                        value: "",
                        feedback: "",
                        showHint: false,
                    }) as ItemState
            )
    );

    useEffect(() => {
        setItems(
            new Array(total).fill(0).map(
                () =>
                    ({
                        value: "",
                        feedback: "",
                        showHint: false,
                    }) as ItemState
            )
        );
    }, [total, exercises, answers]);
    // zachowaj spacje przy wyłuskiwaniu brakującego fragmentu
    const normalizeSpaces = (s: string) =>
        s.toLowerCase().trim().replace(LETTERS_LATIN, "").replace(/\s+/g, " ");

    /** Zwraca brakujący fragment z pełnego zdania na podstawie pozycji "......" w ćwiczeniu. */
    const extractMissingFromFullAnswer = (exercise: string, fullAnswer: string): string | null => {
        if (!exercise.includes("......")) return null;
        const [leftRaw, rightRaw = ""] = exercise.split("......");
        const left = normalizeSpaces(leftRaw);
        const right = normalizeSpaces(rightRaw);
        const ans = normalizeSpaces(fullAnswer);

        // idealnie: ans = left + MISSING + right
        if (ans.startsWith(left) && ans.endsWith(right) && ans.length >= left.length + right.length) {
            const mid = ans.slice(left.length, ans.length - right.length).trim();
            return mid || null;
        }
        // fallback: wyszukaj prawą część później
        const start = ans.indexOf(left);
        if (start === -1) return null;
        const afterLeft = start + left.length;
        const end = ans.indexOf(right, afterLeft);
        if (end === -1) return null;
        const mid = ans.slice(afterLeft, end).trim();
        return mid || null;
    };


    const normalizedAnswers = useMemo(() => {
        const out: string[][] = [];
        for (let i = 0; i < total; i++) {
            const ex = exercises[i] ?? "";
            const raw = answers[i] ?? "";
            const variants = raw.split("|").map(v => v.trim()).filter(Boolean);

            const expectedForThisExercise = variants.map(v => {
                // jeśli answer jest pełnym zdaniem → wydobądź brak; jeśli już jest sam brak, weź go wprost
                const missing = extractMissingFromFullAnswer(ex, v);
                return normalize(missing ?? v);
            }).filter(Boolean);

            out.push(expectedForThisExercise.length ? expectedForThisExercise : [normalize(raw)]);
        }
        return out;
    }, [answers, exercises, total]);


    const checkOne = (index: number) => {
        setItems((prev) => {
            const next = [...prev];
            const user = normalize(next[index].value);
            const variants = normalizedAnswers[index];
            console.log(variants, index)
            const isCorrect = user.length > 0 && variants.includes(user);
            next[index] = {
                ...next[index],
                feedback: (isCorrect ? "correct" : "wrong") as ItemState["feedback"],
            };
            return next;
        });
    };

    const checkAll = () => {
        let correct = 0;
        setItems((prev) => {
            const next: ItemState[] = prev.map((it, i): ItemState => {
                const user = normalize(it.value);
                const isCorrect =
                    user.length > 0 && normalizedAnswers[i]?.includes(user);
                if (isCorrect) correct += 1;
                return {
                    ...it,
                    feedback: (isCorrect ? "correct" : "wrong") as ItemState["feedback"],
                };
            });
            return next;
        });
        onCheckedAll?.({ correct, total });
    };

    const toggleHint = (i: number) => {
        setItems((prev) => {
            const next = [...prev];
            next[i] = { ...next[i], showHint: !next[i].showHint };
            return next;
        });
    };

    return (
        <div className={`w-full ${className}`}>
            <p className="text-sm text-gray-600 mb-3">
                Wstaw brakujące słowo/słowa. Wielkość liter nie ma znaczenia.
            </p>

            <div className="space-y-4">
                {exercises.slice(0, total).map((sentence, i) => {
                    const item = items[i];
                    const hasGap = sentence.includes("......");
                    const answerLabel = answers[i];
                    const showCorrect = showAnswers || item.showHint;

                    return (
                        <div key={i} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                            <div className="flex items-start gap-3">
                                <div className="flex-1">
                                    <p className="text-base leading-relaxed text-gray-800">
                                        {hasGap ? (
                                            <>
                                                {sentence.split("......")[0]}
                                                <span className="inline-block min-w-24 px-2 py-0.5 rounded bg-gray-100 text-gray-500 align-baseline">
                                                    ______
                                                </span>
                                                {sentence.split("......")[1] ?? ""}
                                            </>
                                        ) : (
                                            sentence
                                        )}
                                    </p>

                                    <input
                                        className={`mt-3 w-full rounded-lg border px-3 py-2 text-gray-800 outline-none focus:ring
                    ${item.feedback === "correct"
                                                ? "border-green-500 ring-green-200"
                                                : item.feedback === "wrong"
                                                    ? "border-red-500 ring-red-200"
                                                    : "border-gray-300 focus:border-gray-400 ring-gray-200"
                                            }`}
                                        placeholder="Wpisz odpowiedź"
                                        value={item.value}
                                        onChange={(e) =>
                                            setItems((prev) => {
                                                const next = [...prev];
                                                next[i] = { ...next[i], value: e.target.value, feedback: "" };
                                                return next;
                                            })
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") checkOne(i);
                                        }}
                                        disabled={disabled}
                                    />

                                    {item.feedback !== "" && (
                                        <p
                                            className={`mt-2 text-sm font-semibold ${item.feedback === "correct" ? "text-green-600" : "text-red-600"
                                                }`}
                                            role="status"
                                        >
                                            {item.feedback === "correct"
                                                ? "Poprawna odpowiedź!"
                                                : "Błędna odpowiedź. Spróbuj ponownie."}
                                        </p>
                                    )}

                                    {(showAnswers || answerLabel) && (
                                        <div className="mt-2 flex items-center gap-2">
                                            {!showAnswers && (
                                                <button
                                                    type="button"
                                                    onClick={() => toggleHint(i)}
                                                    className="inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100"
                                                    disabled={disabled}
                                                    aria-pressed={item.showHint}
                                                    title={item.showHint ? "Ukryj podpowiedź" : "Pokaż podpowiedź"}
                                                >
                                                    <Lightbulb size={16} strokeWidth={2.2} />
                                                    {item.showHint ? "Ukryj" : "Podpowiedź"}
                                                </button>
                                            )}

                                            {showCorrect && (
                                                <span className="text-xs text-gray-600">
                                                    <span className="font-semibold">Odpowiedź:</span> {answerLabel}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col gap-2 pl-2">
                                    <button
                                        type="button"
                                        onClick={() => checkOne(i)}
                                        className="rounded-md px-3 py-1.5 text-xs font-medium text-white transition hover:brightness-95 disabled:opacity-60"
                                        style={{ backgroundColor: "var(--book-accent, #F6851F)" }}
                                        disabled={disabled}
                                    >
                                        Sprawdź
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setItems((prev) => {
                                                const next = [...prev];
                                                next[i] = { value: "", feedback: "", showHint: false };
                                                return next;
                                            })
                                        }
                                        className="rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-60"
                                        style={{
                                            borderColor: "var(--book-accent, #F6851F)",
                                            color: "var(--book-accent, #F6851F)",
                                        }}
                                        disabled={disabled}
                                    >
                                        Wyczyść
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {total > 0 && (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={checkAll}
                        className="rounded-md px-4 py-2 text-sm font-semibold text-white hover:brightness-95 disabled:opacity-60"
                        style={{ backgroundColor: "var(--book-accent, #F6851F)" }}
                        disabled={disabled}
                    >
                        Sprawdź wszystko
                    </button>
                    <button
                        type="button"
                        onClick={() =>
                            setItems((prev) => prev.map(() => ({ value: "", feedback: "", showHint: false })))
                        }
                        className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
                        style={{
                            borderColor: "var(--book-accent, #F6851F)",
                            color: "var(--book-accent, #F6851F)",
                        }}
                        disabled={disabled}
                    >
                        Wyczyść wszystko
                    </button>
                </div>
            )}
        </div>
    );
};

export default SentenceExercise;
